import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { LANG_NAMES } from "../i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Logo = () => {
  const { lp } = useLang();
  return (
    <Link to={lp("/")} className="flex items-center gap-2 group">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1e4620] text-white font-bold text-lg transition-transform duration-300 group-hover:scale-105">
        P
      </span>
      <span className="leading-tight">
        <span className="block text-[15px] font-bold tracking-tight text-[#1e4620]">Pure</span>
        <span className="block text-[11px] font-semibold tracking-widest text-neutral-500 -mt-1">INVESTMENTS</span>
      </span>
    </Link>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t, lang, setLang, langName, lp } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex h-[70px] max-w-[1200px] items-center justify-between px-6">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex">
          {t.nav.map((l) => {
            const target = lp(l.path);
            const active = location.pathname === target;
            return (
              <Link
                key={l.path}
                to={target}
                className={`relative text-[15px] font-medium transition-colors duration-200 hover:text-[#F26522] ${
                  active ? "text-[#F26522]" : "text-neutral-700"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-0.5 bg-[#F26522] transition-all duration-300 ${
                    active ? "w-full" : "w-0"
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="hidden items-center gap-1.5 rounded-md border border-neutral-200 px-3 py-1.5 text-sm font-medium text-neutral-700 outline-none transition-colors hover:border-neutral-300 lg:flex">
              {langName}
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.keys(LANG_NAMES).map((code) => (
                <DropdownMenuItem
                  key={code}
                  onClick={() => setLang(code)}
                  className={lang === code ? "font-semibold text-[#F26522]" : ""}
                >
                  {LANG_NAMES[code]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-neutral-800 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden border-t border-neutral-100 bg-white transition-all duration-300 lg:hidden ${
          open ? "max-h-[500px]" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-3">
          {t.nav.map((l) => (
            <Link
              key={l.path}
              to={lp(l.path)}
              className={`border-b border-neutral-50 py-3 text-[15px] font-medium ${
                location.pathname === lp(l.path) ? "text-[#F26522]" : "text-neutral-700"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex gap-2 py-3">
            {Object.keys(LANG_NAMES).map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  lang === code ? "border-[#F26522] text-[#F26522]" : "border-neutral-200 text-neutral-600"
                }`}
              >
                {LANG_NAMES[code]}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate(lp("/contact"))}
            className="mt-1 rounded-md bg-[#F26522] py-2.5 text-sm font-semibold text-white"
          >
            {t.ctaShort}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
